import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryError } from "../services/queryError";
import { track } from "../analytics";
import {
  deleteOrderReview,
  fetchFeaturedReviews,
  fetchOrderReviewState,
  fetchReviewableOrders,
  fetchReviews,
  submitOrderReview,
} from "../services/reviewService";
import {
  normalizeFeaturedReviews,
  normalizeOrderReviewState,
  normalizeReviewableOrders,
  normalizeReviewsResponse,
} from "../components/Reviews/normalize";

const useQueryWithError = (query, { showToast, navigate } = {}) => {
  useEffect(() => {
    if (query.isError) {
      queryError(query.error, showToast, navigate);
    }
  }, [query.isError, query.error, showToast, navigate]);

  return query;
};

const reviewOptions = {
  staleTime: 0,
  gcTime: 5 * 60 * 1000,
  refetchOnMount: "always",
  refetchOnReconnect: true,
  refetchOnWindowFocus: false,
};

export const useReviews = ({ page = 1, limit = 20, ...options } = {}) => {
  const query = useQuery({
    queryKey: ["reviews", { page, limit }],
    queryFn: () => fetchReviews({ page, limit }),
    select: normalizeReviewsResponse,
    ...reviewOptions,
    ...options,
  });

  return useQueryWithError(query, options);
};

export const useFeaturedReviews = (options = {}) =>
  useQuery({
    queryKey: ["reviews", "featured"],
    queryFn: fetchFeaturedReviews,
    select: normalizeFeaturedReviews,
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  });

export const useReviewableOrders = (options = {}) => {
  const query = useQuery({
    queryKey: ["reviewableOrders"],
    queryFn: fetchReviewableOrders,
    select: normalizeReviewableOrders,
    ...reviewOptions,
    ...options,
  });

  return useQueryWithError(query, options);
};

export const useOrderReviewState = (orderId, options = {}) => {
  const query = useQuery({
    queryKey: ["orderReview", orderId],
    queryFn: () => fetchOrderReviewState(orderId),
    select: normalizeOrderReviewState,
    enabled: !!orderId,
    ...reviewOptions,
    ...options,
  });

  return useQueryWithError(query, options);
};

const invalidateReviewData = (queryClient, orderId) => {
  queryClient.invalidateQueries({ queryKey: ["reviews"] });
  queryClient.invalidateQueries({ queryKey: ["reviewableOrders"] });
  queryClient.invalidateQueries({ queryKey: ["products"] });
  queryClient.invalidateQueries({ queryKey: ["bundles"] });
  if (orderId) {
    queryClient.invalidateQueries({ queryKey: ["orderReview", orderId] });
  }
};

export const useSubmitReview = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: submitOrderReview,
    onSuccess: (data, variables) => {
      const payload = variables?.payload;
      track("Submitted Review", {
        order_id: variables?.orderId,
        freshness_rating: payload?.freshnessRating,
        delivery_rating: payload?.deliveryRating,
        support_rating: payload?.supportRating,
        recommendation_rating: payload?.recommendationRating,
        item_ratings_count: payload?.itemRatings?.length || 0,
        has_comment: !!payload?.comment,
        anonymous: !!payload?.anonymous,
      });
      invalidateReviewData(queryClient, variables?.orderId);
      options.onSuccess?.(data, variables);
    },
    onError: options.onError,
  });
};

export const useDeleteReview = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteOrderReview,
    onSuccess: (data, orderId) => {
      track("Deleted Review", { order_id: orderId });
      invalidateReviewData(queryClient, orderId);
      options.onSuccess?.(data, orderId);
    },
    onError: options.onError,
  });
};
